import * as React from 'react';
import {
    Alert, Box, Button, Card, CardContent, CircularProgress, Pagination, Snackbar, Stack, TextField,
    ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { generateVirtualTeam, getVirtualCatalogSports, searchVirtualProviderTeams } from '../Api';
import VirtualCatalogAddMenu from '../components/virtual-sports/VirtualCatalogAddMenu';
import VirtualImmediateGenerationDialog from '../components/virtual-sports/VirtualImmediateGenerationDialog';
import VirtualTeamCreationDialog from '../components/virtual-sports/VirtualTeamCreationDialog';
import VirtualTeamsTable from '../components/virtual-sports/VirtualTeamsTable';
import VirtualActivityLogBell from '../components/virtual-sports/VirtualActivityLogBell';
import VirtualTeamDetailsDialog from '../components/virtual-sports/VirtualTeamDetailsDialog';
import VirtualTeamImportDialog from '../components/virtual-sports/VirtualTeamImportDialog';
import VirtualCountrySelect from '../components/virtual-sports/VirtualCountrySelect';
import { VirtualProviderTeamDto } from '../models/dtos/VirtualProviderCatalogDto';
import { VirtualCatalogSportDto } from '../models/dtos/VirtualSportsCatalogDto';

const PAGE_SIZE = 25;
type TeamStatusFilter = 'ALL' | 'ACTIVE' | 'INACTIVE';

export default function VirtualSportsTeamsPage() {
    const navigate = useNavigate();
    const [loading, setLoading] = React.useState(true);
    const [sports, setSports] = React.useState<VirtualCatalogSportDto[]>([]);
    const [teams, setTeams] = React.useState<VirtualProviderTeamDto[]>([]);
    const [totalItems, setTotalItems] = React.useState(0);
    const [searchTerm, setSearchTerm] = React.useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = React.useState('');
    const [originCountry, setOriginCountry] = React.useState('');
    const [statusFilter, setStatusFilter] = React.useState<TeamStatusFilter>('ALL');
    const [page, setPage] = React.useState(0);
    const [reloadKey, setReloadKey] = React.useState(0);
    const [manualOpen, setManualOpen] = React.useState(false);
    const [immediateOpen, setImmediateOpen] = React.useState(false);
    const [importOpen, setImportOpen] = React.useState(false);
    const [error, setError] = React.useState('');
    const [notice, setNotice] = React.useState('');
    const [selectedTeam, setSelectedTeam] = React.useState<VirtualProviderTeamDto | null>(null);

    React.useEffect(() => {
        getVirtualCatalogSports().then(setSports).catch(reason =>
            setError(reason instanceof Error ? reason.message : 'Could not load virtual sports'));
    }, []);

    React.useEffect(() => {
        const timeout = window.setTimeout(() => {
            setDebouncedSearchTerm(searchTerm.trim());
            setPage(0);
        }, 300);
        return () => window.clearTimeout(timeout);
    }, [searchTerm]);

    React.useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError('');
        searchVirtualProviderTeams({
            offset: page * PAGE_SIZE,
            limit: PAGE_SIZE,
            searchTerm: debouncedSearchTerm || undefined,
            originCountry: originCountry || undefined,
            active: statusFilter === 'ALL' ? undefined : statusFilter === 'ACTIVE',
        }).then(result => {
            if (cancelled) return;
            setTeams(result.items);
            setTotalItems(result.totalItems);
        }).catch(reason => {
            if (!cancelled) setError(reason instanceof Error ? reason.message : 'Could not load virtual teams');
        }).finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [page, debouncedSearchTerm, originCountry, statusFilter, reloadKey]);

    const sportNames = new Map(sports.map(sport => [sport.id, sport.name]));
    const reloadFirstPage = () => {
        if (page === 0) setReloadKey(value => value + 1);
        else setPage(0);
    };
    const created = (team: VirtualProviderTeamDto) => {
        setNotice(`${team.name} was created.`);
        reloadFirstPage();
    };

    return <Stack spacing={3} sx={{ mx: { xs: 2, md: 5 }, my: 5 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <Box sx={{ flex: 1 }}>
                <Typography variant="h3">Virtual teams</Typography>
                <Typography color="text.secondary">{totalItems.toLocaleString()} teams found</Typography>
            </Box>
            <VirtualCatalogAddMenu
                entityLabel="team"
                onAdd={() => setManualOpen(true)}
                onGenerateImmediately={() => setImmediateOpen(true)}
                onImportFromFile={() => setImportOpen(true)}
            />
            <VirtualActivityLogBell />
            <Button variant="outlined" onClick={() => navigate('/virtual-sports')}>Back to overview</Button>
        </Stack>

        {error ? <Alert severity="error">{error}</Alert> : null}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <TextField
                fullWidth
                label="Search by team name or biography"
                type="search"
                value={searchTerm}
                onChange={event => setSearchTerm(event.target.value)}
            />
            <VirtualCountrySelect
                value={originCountry}
                onChange={value => { setOriginCountry(value); setPage(0); }}
                required={false}
                label="Country filter"
            />
            <ToggleButtonGroup
                exclusive
                value={statusFilter}
                onChange={(_, value: TeamStatusFilter | null) => {
                    if (value) { setStatusFilter(value); setPage(0); }
                }}
                size="small"
                sx={{ whiteSpace: 'nowrap' }}
            >
                <ToggleButton value="ALL">All</ToggleButton>
                <ToggleButton value="ACTIVE">Active</ToggleButton>
                <ToggleButton value="INACTIVE">Inactive</ToggleButton>
            </ToggleButtonGroup>
        </Stack>

        <Card variant="outlined"><CardContent>
            {loading ? <Box textAlign="center"><CircularProgress /></Box> : (
                <VirtualTeamsTable teams={teams} sportNames={sportNames} onTeamClick={setSelectedTeam} />
            )}
        </CardContent></Card>

        {totalItems > PAGE_SIZE ? <Pagination
            count={Math.ceil(totalItems / PAGE_SIZE)}
            page={page + 1}
            onChange={(_, value) => setPage(value - 1)}
            sx={{ alignSelf: 'center' }}
        /> : null}

        <VirtualTeamCreationDialog
            open={manualOpen}
            sports={sports}
            onClose={() => setManualOpen(false)}
            onCreated={result => created(result.team)}
        />
        <VirtualImmediateGenerationDialog
            open={immediateOpen}
            entityLabel="team"
            sports={sports}
            onClose={() => setImmediateOpen(false)}
            onGenerate={async (sportId, _count, _onProgress, options) => {
                const result = await generateVirtualTeam(sportId, {
                    countryCode: options.countryCode,
                    description: options.description,
                });
                created(result.team);
            }}
        />
        <VirtualTeamImportDialog
            open={importOpen}
            onClose={() => setImportOpen(false)}
            onImported={result => {
                setNotice(`${result.importedCount} ${result.importedCount === 1 ? 'team was' : 'teams were'} imported.`);
                reloadFirstPage();
            }}
        />
        <VirtualTeamDetailsDialog
            team={selectedTeam}
            sportNames={sportNames}
            onClose={() => setSelectedTeam(null)}
            onUpdated={updated => {
                setSelectedTeam(updated);
                setNotice(`${updated.name} was updated.`);
                setReloadKey(value => value + 1);
            }}
        />
        <Snackbar open={Boolean(notice)} autoHideDuration={5000} onClose={() => setNotice('')} message={notice} />
    </Stack>;
}
